import { ChangeEvent, useCallback, useMemo, useState } from "react";

import Fuse from "fuse.js";
import { WorkspaceQuery } from "@gql/sites-api/graphql";
import { isDefined } from "react-migration/lib/util/isDefined";

type WorkspaceFromQuery = NonNullable<WorkspaceQuery["workspace"]>;

const SEARCHABLE_WORKSPACE_FIELDS: (keyof WorkspaceFromQuery)[] = ["title", "goal", "developmentType"];

interface UseWorkspaceSearchProps {
  list: WorkspaceFromQuery[];
  selected: WorkspaceFromQuery | null | undefined;
}

export function useWorkspaceSearch({ list, selected }: UseWorkspaceSearchProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const workspaceSearchIndex = useMemo(() => {
    const allWorkspaces = [selected, ...list].filter(isDefined);

    if (!allWorkspaces.length) return null;

    return new Fuse(allWorkspaces, {
      ignoreLocation: true,
      isCaseSensitive: false,
      threshold: 0.3,
      keys: SEARCHABLE_WORKSPACE_FIELDS,
    });
  }, [selected, list]);

  const filteredList = useMemo(() => {
    if (!searchQuery.trim() || !workspaceSearchIndex) {
      return [selected, ...list].filter(isDefined);
    }

    return workspaceSearchIndex
      .search(searchQuery)
      .map((result) => result.item)
      .sort((a, b) => {
        // if selected workspace matches, move it to the top
        if (selected?.id === a.id) return -1;
        if (selected?.id === b.id) return 1;
        return 0;
      });
  }, [searchQuery, workspaceSearchIndex, selected, list]);

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  return {
    searchQuery,
    filteredList,
    handleSearchChange,
  };
}
